export interface ApiCompetenceRef {
  code: string;
}

export interface ApiCompetence extends ApiCompetenceRef {}

export interface ApiCourseProductRef {
  code: string;
}

export interface ApiCourseProduct extends ApiCourseProductRef {
  name: string;
}

export interface ApiCourseRef {
  code: string;
  product: ApiCourseProductRef;
}

export interface ApiScheduleEventModel {}

export interface LessonReference {
  lessonId: number;
}

export interface ApiLessonEventModel extends ApiScheduleEventModel {
  lesson: LessonReference;
}

export interface EventTime {
  eventDate: Date;
  duration: number;
}

export interface EventReference extends EventTime {
  eventId: number;
}

export interface ScheduleEventReference<EM extends ApiScheduleEventModel> extends EventReference {
  eventData: EM;
}

export interface LessonScheduleEventReference<EM extends ApiLessonEventModel> extends ScheduleEventReference<EM> {}

export interface ColLessonScheduleEventReference extends LessonScheduleEventReference<ApiLessonEventModel> {}

export interface ScheduleCancellationRequest {
  reason: string;
}

export enum LessonFlagType {
  PoorConnection,
  ClassroomIssue,
  StudentNeedsHelp,
  Other,
  StudentNotShow,
  TeacherNotShow
}

export interface ColScheduleInvalidationRequest extends ScheduleCancellationRequest {
  flags: LessonFlagType[];
}

export interface GroupDetails {
  name: string;
}

export interface SchoolPersonRef {
  id: number;
}

export interface SchoolMemberRef extends SchoolPersonRef {}

export interface GroupMemberRef {
  id: number;
}

export interface GroupMember extends GroupMemberRef {
  member: SchoolMemberRef;
}

export interface GroupMemberBase<SM extends SchoolMemberRef> extends GroupMember {
  member: SM;
}

export interface GroupRef {
  id: number;
}

export interface Lesson extends LessonReference {
  status: string;
}

export interface LessonAlarm {
  eventType: string;
  scheduleId: number;
  lessonId: number;
}

export interface ScheduleTimeDetails {
  starting: Date;
  timezone: string;
}

export interface NonRecurringScheduleDefinition {
  duration: number;
  time: ScheduleTimeDetails;
}

export interface PersonRef {
  id: number;
}

export interface PersonDetails {
  name: string;
  surname: string;
  emailAddress: string;
}

export interface PersonAccountRef {
  accountId: number;
}

export interface PersonDetailedRef extends PersonRef, PersonDetails, PersonAccountRef {}

export interface Person extends PersonRef, PersonDetails, PersonAccountRef {}

export interface ScheduleReference {
  id: number;
  name: string;
}

export interface ScheduleEvents<S extends ScheduleReference, E extends EventReference> {
  schedule: S;
  events: E[];
}

export interface ProductAvailabilityDetails {
  product: ApiCourseProduct;
  competences: ApiCompetence[];
  overtake: number;
}

export interface ProductAvailability<S extends ScheduleReference, E extends EventReference> extends ScheduleEvents<S, E> {
  details: ProductAvailabilityDetails;
}

export interface ScheduleDefinition extends NonRecurringScheduleDefinition {
  ending: Date;
  recurring: string;
}

export interface ProductAvailabilityRequest {
  scheduleDetails: ScheduleDefinition;
  availabilityDetails: ProductAvailabilityDetails;
}

export interface SchoolDetails {
  name: string;
}

export interface SchoolMemberDetailsBase<P extends PersonRef> {
  person: P;
}

export interface SchoolMemberPersonDetailed extends SchoolMemberDetailsBase<PersonDetailedRef> {}

export interface SchoolMemberPersonRef extends SchoolMemberDetailsBase<PersonRef> {}

export interface SchoolRef {
  id: number;
}

export interface TeacherRef extends SchoolMemberRef {
  id: number;
}

export interface SimpleLessonScheduleRequest {
  name: string;
  time: ScheduleTimeDetails;
  duration: number;
  course: ApiCourseRef;
  competence: ApiCompetenceRef;
  teacher: TeacherRef;
}

export interface SimpleProductAvailability extends ProductAvailability<ScheduleReference, EventReference> {}

export interface TeacherProductAvailability<T extends TeacherRef, E extends EventTime> {
  name: string;
  teacher: T;
  details: ProductAvailabilityDetails;
  events: E[];
}

export interface V3Teacher extends TeacherRef, SchoolMemberPersonDetailed {}

export interface SimpleTeacherProductAvailability extends TeacherProductAvailability<V3Teacher, EventTime> {}

export interface StudentColLessonScheduleEvents extends ScheduleEvents<ScheduleReference, ColLessonScheduleEventReference> {}

export interface StudentRef extends SchoolMemberRef {
  id: number;
}

export interface TeacherColLessonScheduleEvents extends ScheduleEvents<ScheduleReference, ColLessonScheduleEventReference> {}

export interface V3Group extends GroupRef, GroupDetails {}

export interface V3Student extends StudentRef, SchoolMemberPersonDetailed {}

export interface V3GroupStudent extends GroupMemberBase<V3Student> {}

export interface V3GroupTeacher extends GroupMemberBase<V3Teacher> {}

export interface V3School extends SchoolRef, SchoolDetails {}

export enum RecurringType {
  Single,
  Daily,
  Weekly,
  Monthly,
}

export class RecurringTypeUtils {
  public static values = [
    RecurringType.Single,
    RecurringType.Daily,
    RecurringType.Weekly,
    RecurringType.Monthly,
  ];

  static days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  public static getName(type: RecurringType) {
    switch (type) {
      case RecurringType.Single:
        return 'COL.TEACHER.CREATE_WORKTIME.RECURRING_TYPES.SINGLE';
      case RecurringType.Daily:
        return 'COL.TEACHER.CREATE_WORKTIME.RECURRING_TYPES.DAILY';
      case RecurringType.Weekly:
        return 'COL.TEACHER.CREATE_WORKTIME.RECURRING_TYPES.WEEKLY';
      case RecurringType.Monthly:
        return 'COL.TEACHER.CREATE_WORKTIME.RECURRING_TYPES.MONTHLY';
    }
  }

  public static getRecurring(recurringType: RecurringType, weekDays: string[]) {
    let recurring: string;
    const type = this.values[+recurringType];
    switch (type) {
      case RecurringType.Single:
        recurring = null;
        break;
      case RecurringType.Monthly:
        recurring = "m:1";
        break;
      case RecurringType.Daily:
      case RecurringType.Weekly:
        recurring = "w:1";
        break;
    }
    if (type === RecurringType.Daily && weekDays.length > 0) {
      weekDays.forEach(d => {
        const dayIndex = this.days.indexOf(d);
        if (dayIndex > -1) recurring = recurring.concat(`:${dayIndex + 1}`);
      });
    }
    return recurring;
  }
}
